import storage from "./Storage";
import {
    getUserIdService,
    PollResponse,
    UserIdResponse,
} from "../Services/AuthService";
import { userReactionsType } from "@infrastructure/Hooks/UserInteractions/UserInteractions.types";
import Storage from "@infrastructure/Persistence/Storage";
import { useUserCommentedPostsStore } from "@infrastructure/Hooks/UserInteractions/useUserCommentedPostsStore";

const USER_ID_KEY = "userId";
const POLL_KEY = "polls";
const LOCAL_STORAGE_UNIXTIME_KEY = "localStorageUnixtime";
const REACTIONS_KEY = "reactions";
const STORED_POST_SORT_CRITERIA_KEY = "posts-selectedSortingCriteria";
const STORED_COMMENT_SORT_CRITERIA_KEY = "comments-selectedSortingCriteria";
const CONFIG_VERSION = "configVersion";

let isLoading = false;

export function useUpdateUserLocalStorageFromBE() {
    const { addToCommentedPosts } = useUserCommentedPostsStore();

    async function getUserPersonalizedDataFromBE() {
        const localUserId = getLocalStorageUserId();

        if (localUserId) {
            return localUserId;
        }

        if (!isLoading) {
            isLoading = true;
            const userId = await getUserIdService().then((data) => {
                isLoading = false;
                setLocalStorageUserData(data);
                data.interactions.postsComments.forEach((postComments) => {
                    addToCommentedPosts(
                        postComments.postId,
                        postComments.commentsCount,
                    );
                });
                return data.userId;
            });
            return userId;
        }
    }

    return { getUserPersonalizedDataFromBE };
}

export function getLocalStorageUserId() {
    return storage.getItem(USER_ID_KEY) ?? undefined;
}

export function deleteLocalStorageUserData() {
    storage.removeItem(LOCAL_STORAGE_UNIXTIME_KEY);
    storage.removeItem(USER_ID_KEY);
    storage.removeItem(POLL_KEY);
    storage.removeItem(REACTIONS_KEY);
}

export function setLocalStorageUserData(data: UserIdResponse) {
    storage.setItem(LOCAL_STORAGE_UNIXTIME_KEY, Date.now().toString());
    storage.setItem(USER_ID_KEY, data.userId);
    storage.setItem(POLL_KEY, JSON.stringify(data?.interactions?.polls ?? []));
    storage.setItem(
        REACTIONS_KEY,
        JSON.stringify(
            data.interactions?.reactions?.map((reaction) => ({
                ...reaction,
                objectId: reaction.objectId.toString(),
            })) ?? [],
        ),
    );
}

export function getLocalStoragePolls(): PollResponse[] {
    const pollsJson = storage.getItem(POLL_KEY) ?? "[]";
    return JSON.parse(pollsJson);
}

export function setLocalStoragePolls(newPolls: PollResponse[]): void {
    storage.setItem(POLL_KEY, JSON.stringify(newPolls));
}

export function getLocalStorageReactions(): userReactionsType[] {
    const reactionsString = storage.getItem(REACTIONS_KEY) ?? "[]";
    return JSON.parse(reactionsString);
}

export function setLocalStorageReactions(newReactions: userReactionsType[]) {
    storage.setItem(REACTIONS_KEY, JSON.stringify(newReactions));
}

export function setLocalSelectedPostSortOption(index: number) {
    Storage.setItem(STORED_POST_SORT_CRITERIA_KEY, index.toString());
}

export function getLocalSelectedPostSortOption() {
    return parseInt(Storage.getItem(STORED_POST_SORT_CRITERIA_KEY) ?? "0");
}

export function setLocalSelectedCommentSortOption(index: number) {
    Storage.setItem(STORED_COMMENT_SORT_CRITERIA_KEY, index.toString());
}

export function getLocalSelectedCommentSortOption() {
    return parseInt(Storage.getItem(STORED_COMMENT_SORT_CRITERIA_KEY) ?? "0");
}

export function setLocalStorageVersion(version: number) {
    Storage.setItem(CONFIG_VERSION, version.toString());
}

export function getLocalStorageVersion(): number {
    const version = Storage.getItem(CONFIG_VERSION);
    if (!version) {
        return 0;
    }
    return parseInt(version);
}

export function handleLocalStorageVersion(newVersion: number): void {
    if (newVersion === getLocalStorageVersion()) {
        return;
    }
    Storage.clear();
    setLocalStorageVersion(newVersion);
}
